
import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import Papa from 'papaparse'

interface Microcuenca {
  _id?: string
  fid: string
  codigo: string
  municipio: string
  microcuenca: string
  superficie_agricola: number
  sup_micro: number
  superficie_pasto: number
  superficie_forestal: number
  densidad_poblacion: number
  precipitacion: number
  temperatura: number
  altitud: number
  pendiente: number
  createdAt?: string
  updatedAt?: string
  creator?: string
}

interface CSVRow {
  fid: string
  CODIGO: string
  MUNICIPIO: string
  MICROCUENCA: string
  'Superficie Agricola': string
  'Sup_Micro_': string
  'Superficie Pasto': string
  'Superficie Forestal': string
  'Densidad de población': string
  'Precipitación': string
  'Temperatura': string
  'Altitud': string
  'Pendiente': string
}

export const useMicrocuencasCRUD = () => {
  const [microcuencas, setMicrocuencas] = useState<Microcuenca[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const fetchMicrocuencas = useCallback(async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.microcuencas.list({
        sort: { createdAt: -1 }
      })
      setMicrocuencas(response.list || [])
    } catch (error: any) {
      console.error('Error al cargar microcuencas:', error)
      toast.error('Error al cargar los datos')
    } finally {
      setLoading(false)
    }
  }, [])

  const createMicrocuenca = async (data: Omit<Microcuenca, '_id'>) => {
    try {
      const newRecord = await lumi.entities.microcuencas.create({
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'user'
      })
      setMicrocuencas(prev => [newRecord, ...prev])
      toast.success('Microcuenca creada exitosamente')
      return newRecord
    } catch (error: any) {
      console.error('Error al crear microcuenca:', error)
      toast.error('Error al crear el registro')
      throw error
    }
  }

  const updateMicrocuenca = async (id: string, updates: Partial<Microcuenca>) => {
    try {
      const updatedRecord = await lumi.entities.microcuencas.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      setMicrocuencas(prev => prev.map(m => m._id === id ? updatedRecord : m))
      toast.success('Microcuenca actualizada exitosamente')
      return updatedRecord
    } catch (error: any) {
      console.error('Error al actualizar microcuenca:', error)
      toast.error('Error al actualizar el registro')
      throw error
    }
  }

  const deleteMicrocuenca = async (id: string) => {
    try {
      await lumi.entities.microcuencas.delete(id)
      setMicrocuencas(prev => prev.filter(m => m._id !== id))
      toast.success('Microcuenca eliminada exitosamente')
    } catch (error: any) {
      console.error('Error al eliminar microcuenca:', error)
      toast.error('Error al eliminar el registro')
      throw error
    }
  }

  const processCSVData = (csvData: CSVRow[]): Omit<Microcuenca, '_id'>[] => {
    return csvData.map(row => ({
      fid: row.fid || '',
      codigo: row.CODIGO || '',
      municipio: row.MUNICIPIO || '',
      microcuenca: row.MICROCUENCA || '',
      superficie_agricola: parseFloat(row['Superficie Agricola']) || 0,
      sup_micro: parseFloat(row['Sup_Micro_']) || 0,
      superficie_pasto: parseFloat(row['Superficie Pasto']) || 0,
      superficie_forestal: parseFloat(row['Superficie Forestal']) || 0,
      densidad_poblacion: parseFloat(row['Densidad de población']) || 0,
      precipitacion: parseFloat(row['Precipitación']) || 0,
      temperatura: parseFloat(row['Temperatura']) || 0,
      altitud: parseFloat(row['Altitud']) || 0,
      pendiente: parseFloat(row['Pendiente']) || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      creator: 'csv_import'
    }))
  }

  const uploadCSVFile = async (file: File) => {
    setUploading(true)
    try {
      return new Promise<void>((resolve, reject) => {
        Papa.parse<CSVRow>(file, {
          header: true,
          skipEmptyLines: true,
          complete: async (results) => {
            try {
              if (results.errors.length > 0) {
                console.warn('Errores en el CSV:', results.errors)
              }

              const processedData = processCSVData(results.data)
              
              if (processedData.length === 0) {
                toast.error('No se encontraron datos válidos en el archivo')
                reject(new Error('No valid data found'))
                return
              }

              // Crear registros en lotes para mejor rendimiento
              const batchSize = 10
              let successCount = 0
              
              for (let i = 0; i < processedData.length; i += batchSize) {
                const batch = processedData.slice(i, i + batchSize)
                
                try {
                  const promises = batch.map(data => 
                    lumi.entities.microcuencas.create(data)
                  )
                  await Promise.all(promises)
                  successCount += batch.length
                } catch (batchError) {
                  console.error(`Error en lote ${i / batchSize + 1}:`, batchError)
                }
              }

              if (successCount > 0) {
                toast.success(`${successCount} registros importados exitosamente`)
                await fetchMicrocuencas() // Recargar datos
              } else {
                toast.error('No se pudieron importar los registros')
              }

              resolve()
            } catch (error) {
              console.error('Error procesando CSV:', error)
              toast.error('Error al procesar el archivo CSV')
              reject(error)
            }
          },
          error: (error) => {
            console.error('Error parseando CSV:', error)
            toast.error('Error al leer el archivo CSV')
            reject(error)
          }
        })
      })
    } catch (error) {
      console.error('Error en carga de CSV:', error)
      toast.error('Error al cargar el archivo')
      throw error
    } finally {
      setUploading(false)
    }
  }

  const uploadCSVFromURL = async (url: string) => {
    setUploading(true)
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Error al descargar el archivo')
      }
      const csvText = await response.text()
      
      return new Promise<void>((resolve, reject) => {
        Papa.parse<CSVRow>(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: async (results) => {
            try {
              if (results.errors.length > 0) {
                console.warn('Errores en el CSV:', results.errors)
              }

              const processedData = processCSVData(results.data)
              
              if (processedData.length === 0) {
                toast.error('No se encontraron datos válidos en el archivo')
                reject(new Error('No valid data found'))
                return
              }

              const batchSize = 10
              let successCount = 0
              
              for (let i = 0; i < processedData.length; i += batchSize) {
                const batch = processedData.slice(i, i + batchSize)
                
                try {
                  const promises = batch.map(data => 
                    lumi.entities.microcuencas.create(data)
                  )
                  await Promise.all(promises)
                  successCount += batch.length
                } catch (batchError) {
                  console.error(`Error en lote ${i / batchSize + 1}:`, batchError)
                }
              }

              if (successCount > 0) {
                toast.success(`${successCount} registros importados exitosamente desde URL`)
                await fetchMicrocuencas()
              } else {
                toast.error('No se pudieron importar los registros')
              }

              resolve()
            } catch (error) {
              console.error('Error procesando CSV:', error)
              toast.error('Error al procesar el archivo CSV')
              reject(error)
            }
          },
          error: (error) => {
            console.error('Error parseando CSV:', error)
            toast.error('Error al leer el archivo CSV')
            reject(error)
          }
        })
      })
    } catch (error) {
      console.error('Error en carga de CSV desde URL:', error)
      toast.error('Error al cargar el archivo desde la URL')
      throw error
    } finally {
      setUploading(false)
    }
  }

  const clearAllData = async () => {
    try {
      const ids = microcuencas.map(m => m._id!).filter(Boolean)
      if (ids.length > 0) {
        // Procesar en lotes de 20 para evitar URLs muy largas
        const batchSize = 20
        let deletedCount = 0
        
        for (let i = 0; i < ids.length; i += batchSize) {
          const batch = ids.slice(i, i + batchSize)
          try {
            await lumi.entities.microcuencas.deleteMany(batch)
            deletedCount += batch.length
          } catch (batchError) {
            console.error(`Error eliminando lote ${i / batchSize + 1}:`, batchError)
          }
        }
        
        setMicrocuencas([])
        toast.success(`${deletedCount} registros eliminados exitosamente`)
      }
    } catch (error: any) {
      console.error('Error al limpiar datos:', error)
      toast.error('Error al limpiar los datos')
      throw error
    }
  }

  const removeDuplicates = async () => {
    try {
      const codigoMap = new Map<string, Microcuenca[]>()
      
      // Agrupar por código
      microcuencas.forEach(m => {
        if (!codigoMap.has(m.codigo)) {
          codigoMap.set(m.codigo, [])
        }
        codigoMap.get(m.codigo)!.push(m)
      })
      
      // Identificar duplicados
      const idsToDelete: string[] = []
      codigoMap.forEach((items, codigo) => {
        if (items.length > 1) {
          // Mantener el más reciente, eliminar los demás
          const sorted = items.sort((a, b) => 
            new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
          )
          // Eliminar todos excepto el primero
          idsToDelete.push(...sorted.slice(1).map(item => item._id!).filter(Boolean))
        }
      })
      
      if (idsToDelete.length > 0) {
        // Procesar en lotes de 50 para evitar URLs muy largas
        const batchSize = 50
        let deletedCount = 0
        
        for (let i = 0; i < idsToDelete.length; i += batchSize) {
          const batch = idsToDelete.slice(i, i + batchSize)
          try {
            await lumi.entities.microcuencas.deleteMany(batch)
            deletedCount += batch.length
          } catch (batchError) {
            console.error(`Error eliminando lote ${i / batchSize + 1}:`, batchError)
          }
        }
        
        setMicrocuencas(prev => prev.filter(m => !idsToDelete.includes(m._id!)))
        toast.success(`${deletedCount} registros duplicados eliminados`)
      } else {
        toast.success('No se encontraron duplicados')
      }
    } catch (error: any) {
      console.error('Error al eliminar duplicados:', error)
      toast.error('Error al eliminar duplicados')
      throw error
    }
  }

  useEffect(() => {
    fetchMicrocuencas()
  }, [fetchMicrocuencas])

  return {
    microcuencas,
    loading,
    uploading,
    fetchMicrocuencas,
    createMicrocuenca,
    updateMicrocuenca,
    deleteMicrocuenca,
    uploadCSVFile,
    uploadCSVFromURL,
    clearAllData,
    removeDuplicates
  }
}
